import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { License, LicenseLimits } from "@/types";

function useLicensesClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

function useInvalidateLicenses() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.resource("licenses") });
}

export interface CreateLicenseInput {
  customerId: string;
  edition: string;
  features: string[];
  limits: LicenseLimits;
  validUntil: string; // ISO
  trial?: boolean;
}

export function useCreateLicense(): UseMutationResult<License, Error, CreateLicenseInput> {
  const getClient = useLicensesClient();
  const invalidate = useInvalidateLicenses();
  return useMutation({
    mutationFn: (input) => getClient().post<License>("/licenses", input),
    onSuccess: invalidate,
  });
}

export function useRevokeLicense(): UseMutationResult<License, Error, string> {
  const getClient = useLicensesClient();
  const invalidate = useInvalidateLicenses();
  return useMutation({
    mutationFn: (id) => getClient().post<License>(`/licenses/${id}/revoke`, {}),
    onSuccess: invalidate,
  });
}

export function useSuspendLicense(): UseMutationResult<
  License,
  Error,
  { id: string; suspended: boolean }
> {
  const getClient = useLicensesClient();
  const invalidate = useInvalidateLicenses();
  return useMutation({
    mutationFn: ({ id, suspended }) =>
      getClient().post<License>(`/licenses/${id}/suspend`, { suspended }),
    onSuccess: invalidate,
  });
}

export function useExtendLicense(): UseMutationResult<
  License,
  Error,
  { id: string; validUntil: string; trial?: boolean }
> {
  const getClient = useLicensesClient();
  const invalidate = useInvalidateLicenses();
  return useMutation({
    mutationFn: ({ id, validUntil, trial }) =>
      getClient().post<License>(`/licenses/${id}/extend`, { validUntil, ...(trial === undefined ? {} : { trial }) }),
    onSuccess: invalidate,
  });
}
